import { useMemo } from 'react';

import { PROMPT_MENU_TYPE } from 'src/components/RichSlate';

import {
  createDisabledResourceItem,
  createDocumentResourceItem,
  createImageResourceItem,
  createUrlResourceItem,
} from '../../utils';
import { PromptMenuItem } from '../PromptMenuItem';

export const useMenus = ({
  editor,
  menuType,
  filteredUrlItems,
  filteredBankItems,
  filteredUrlResources,
  filteredDocumentResources,
  filteredImageResources,
  onBankMessageClick,
  onUrlClick,
  onImageClick,
  onDocumentClick,
  noTags,
  trackingEvents,
  onCloseMenu,
  isApplicationModeInfinity,
  openConfirmPopup,
  onOpenResourceInNewTab,
  quickSearchMode,
}) => {
  const messagingBankMenu = useMemo(
    () =>
      filteredBankItems?.map((item, index) => (
        <PromptMenuItem
          type={PROMPT_MENU_TYPE.bank}
          item={item}
          index={index}
          onBankMessageClick={onBankMessageClick}
          editor={editor}
          menuType={menuType}
          trackingEvents={trackingEvents}
          onCloseMenu={onCloseMenu}
          key={item.id}
          isApplicationModeInfinity={isApplicationModeInfinity}
          quickSearchMode={quickSearchMode}
        />
      )),
    [
      filteredBankItems,
      onBankMessageClick,
      menuType,
      trackingEvents,
      onCloseMenu,
      isApplicationModeInfinity,
      editor,
      quickSearchMode,
    ]
  );

  const documentItems = useMemo(
    () => filteredDocumentResources?.map(createDocumentResourceItem),
    [filteredDocumentResources]
  );

  const documentMenu = useMemo(
    () =>
      (documentItems || []).map((item) => (
        <PromptMenuItem
          type={item.type}
          item={item}
          onDocumentClick={onDocumentClick}
          trackingEvents={trackingEvents}
          noTags={noTags}
          key={item.id}
          openConfirmPopup={openConfirmPopup}
          onOpenResourceInNewTab={onOpenResourceInNewTab}
          quickSearchMode={quickSearchMode}
          menuType={menuType}
        />
      )),
    [
      trackingEvents,
      noTags,
      documentItems,
      onDocumentClick,
      openConfirmPopup,
      onOpenResourceInNewTab,
      quickSearchMode,
      menuType,
    ]
  );

  const imageItems = useMemo(
    () => filteredImageResources?.map(createImageResourceItem),
    [filteredImageResources]
  );

  const imagesMenu = useMemo(
    () =>
      (imageItems || []).map((item) => (
        <PromptMenuItem
          type={item.type}
          item={item}
          onImageClick={onImageClick}
          trackingEvents={trackingEvents}
          noTags={noTags}
          key={item.id}
          openConfirmPopup={openConfirmPopup}
          onOpenResourceInNewTab={onOpenResourceInNewTab}
          quickSearchMode={quickSearchMode}
          menuType={menuType}
        />
      )),
    [
      trackingEvents,
      noTags,
      imageItems,
      onImageClick,
      openConfirmPopup,
      onOpenResourceInNewTab,
      quickSearchMode,
      menuType,
    ]
  );

  const urlItems = useMemo(() => {
    const filteredItems =
      menuType === PROMPT_MENU_TYPE.url ? filteredUrlItems : filteredUrlResources;
    const items =
      filteredItems?.length === 0
        ? [createDisabledResourceItem('No saved resources yet')]
        : filteredItems?.map((item) => createUrlResourceItem(item, onUrlClick, noTags));
    return items;
  }, [onUrlClick, noTags, filteredUrlItems, filteredUrlResources, menuType]);

  const urlsMenu = useMemo(
    () =>
      (urlItems || []).map((item) => (
        <PromptMenuItem
          type={item.type}
          item={item}
          onUrlClick={onUrlClick}
          trackingEvents={trackingEvents}
          noTags={noTags}
          key={item.id}
          quickSearchMode={quickSearchMode}
          menuType={menuType}
        />
      )),
    [urlItems, onUrlClick, trackingEvents, noTags, quickSearchMode, menuType]
  );

  return {
    messagingBankMenu,
    documentMenu,
    imagesMenu,
    urlsMenu,
    urlItems,
    documentItems,
    imageItems,
  };
};
